import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import api from "@/api";

export default function FileUploader({
    model_type,
    model_id,
    onFileUpload,
    uploadUrl = "/attachments",
    uploadHeaders = {},
    acceptedFileTypes = "*",
    maxFileSize = 50 * 1024 * 1024, // 50MB default
    multiple = true,
    title = "File Upload",
}) {
    const [uploadError, setUploadError] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [description, setDescription] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);
    const fileInputRef = useRef(null);

    const debugMode =
        import.meta.env.VITE_DEBUG_MODE === "true" ||
        import.meta.env.VITE_DEBUG_MODE === true;

    // Upload function for files
    async function uploadFile(file, index) {
        if (!model_type || !model_id) {
            const error = "Missing model_type or model_id for upload";
            setUploadError(error);
            return { error };
        }

        // Validate file size
        if (file.size > maxFileSize) {
            const error = `File "${file.name}" exceeds maximum size of ${(
                maxFileSize /
                1024 /
                1024
            ).toFixed(0)}MB`;
            setUploadError(error);
            return { error };
        }

        const formData = new FormData();
        formData.append("model_type", model_type);
        formData.append("model_id", model_id);
        if (description && description.trim() !== "") {
            formData.append("description", description.trim());
        }
        formData.append("file", file);

        try {
            // Update progress for this file
            setUploadProgress((prev) => {
                const newProgress = [...prev];
                newProgress[index] = { name: file.name, progress: 0, status: "uploading" };
                return newProgress;
            });

            const response = await api.post(uploadUrl, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    ...uploadHeaders,
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress((prev) => {
                        const newProgress = [...prev];
                        newProgress[index] = {
                            name: file.name,
                            progress: percentCompleted,
                            status: "uploading",
                        };
                        return newProgress;
                    });
                },
            });

            // Mark as completed
            setUploadProgress((prev) => {
                const newProgress = [...prev];
                newProgress[index] = {
                    name: file.name,
                    progress: 100,
                    status: "completed",
                };
                return newProgress;
            });

            setUploadError(null);
            if (debugMode) {
                console.log("Upload successful:", response.data);
            }
            return response.data;
        } catch (err) {
            const msg = err?.response?.data?.message || err.message;

            // Mark as failed
            setUploadProgress((prev) => {
                const newProgress = [...prev];
                newProgress[index] = {
                    name: file.name,
                    progress: 0,
                    status: "failed",
                    error: msg,
                };
                return newProgress;
            });

            setUploadError(msg);
            if (debugMode) {
                console.error("Upload error:", err);
            }
            return { error: msg };
        }
    }

    // Handle file selection (via input or drop)
    async function handleFiles(files) {
        if (!files || files.length === 0) return;

        setIsUploading(true);
        setUploadProgress([]);
        setUploadError(null);

        const fileArray = Array.from(files);
        const results = [];

        // Upload files sequentially or in parallel
        for (let i = 0; i < fileArray.length; i++) {
            const result = await uploadFile(fileArray[i], i);
            results.push(result);
        }

        setIsUploading(false);
        setUploadResult(results);

        // Call parent callback with results
        if (onFileUpload) {
            onFileUpload(results);
        }

        // Clear description after upload
        setDescription("");

        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }

    // Handle drag events
    function handleDragEnter(e) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }

    function handleDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }

    function handleDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    function handleDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        handleFiles(files);
    }

    // Handle file input change
    function handleFileInputChange(e) {
        const files = e.target.files;
        handleFiles(files);
    }

    // Open file picker
    function openFilePicker() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    return (
        <div className="flex flex-col gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
            <h3 className="text-lg font-semibold dark:text-gray-100">{title}</h3>

            {/* Description input */}
            <div className="flex flex-col">
                <label
                    htmlFor="file-description"
                    className="text-sm text-gray-700 dark:text-gray-300 mb-1"
                >
                    Description (optional):
                </label>
                <textarea
                    id="file-description"
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    rows={2}
                    placeholder="Add a description for your files..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isUploading}
                />
            </div>

            {/* Drop zone */}
            <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFilePicker}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${isDragging
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                        : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/20 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-900/40"
                    } ${isUploading ? "pointer-events-none opacity-50" : ""}`}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={acceptedFileTypes}
                    multiple={multiple}
                    onChange={handleFileInputChange}
                    disabled={isUploading}
                />

                <div className="flex flex-col items-center gap-3">
                    {/* Upload icon */}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className={`w-12 h-12 ${isDragging
                                ? "text-blue-500 dark:text-blue-400"
                                : "text-gray-400 dark:text-gray-500"
                            }`}
                    >
                        <path
                            fillRule="evenodd"
                            d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z"
                            clipRule="evenodd"
                        />
                    </svg>

                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {isUploading ? (
                            <span className="font-semibold">Uploading...</span>
                        ) : isDragging ? (
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                                Drop files here
                            </span>
                        ) : (
                            <>
                                <span className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                                    Click to select files
                                </span>
                                <span> or drag and drop</span>
                            </>
                        )}
                    </div>

                    {acceptedFileTypes !== "*" && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            Accepted: {acceptedFileTypes}
                        </div>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        Max size: {(maxFileSize / 1024 / 1024).toFixed(0)}MB
                        {multiple ? " per file" : ""}
                    </div>
                </div>
            </div>

            {/* Upload progress */}
            {uploadProgress.length > 0 && (
                <div className="space-y-2">
                    {uploadProgress.map((item, index) => (
                        <div key={index} className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                                    {item.name}
                                </span>
                                <span
                                    className={`text-xs font-semibold ml-2 ${item.status === "completed"
                                            ? "text-green-600 dark:text-green-400"
                                            : item.status === "failed"
                                                ? "text-red-600 dark:text-red-400"
                                                : "text-blue-600 dark:text-blue-400"
                                        }`}
                                >
                                    {item.status === "completed"
                                        ? "✓ Completed"
                                        : item.status === "failed"
                                            ? "✗ Failed"
                                            : `${item.progress}%`}
                                </span>
                            </div>
                            {item.status === "uploading" && (
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${item.progress}%` }}
                                    />
                                </div>
                            )}
                            {item.status === "failed" && item.error && (
                                <div className="text-xs text-red-600 dark:text-red-400">
                                    {item.error}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Error message */}
            {uploadError && !isUploading && (
                <div className="text-red-600 dark:text-red-400 text-sm text-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    {uploadError}
                </div>
            )}

            {/* Success message */}
            {uploadResult && !isUploading && !uploadError && (
                <div className="text-green-600 dark:text-green-400 text-sm text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    {uploadResult.length} file{uploadResult.length !== 1 ? "s" : ""}{" "}
                    uploaded successfully!
                </div>
            )}
        </div>
    );
}

FileUploader.propTypes = {
    model_type: PropTypes.string.isRequired,
    model_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    onFileUpload: PropTypes.func,
    uploadUrl: PropTypes.string,
    uploadHeaders: PropTypes.object,
    acceptedFileTypes: PropTypes.string,
    maxFileSize: PropTypes.number,
    multiple: PropTypes.bool,
    title: PropTypes.string,
};
